/**
 * Documento HTML autocontenido para `kiosko:printHtml` (Electron).
 * Mantener alineado con `voucher-print.css` y `.voucherContainer` en SCSS.
 */
const VOUCHER_PRINT_CSS = `
.voucher-print-root {
  margin: 3mm !important;
  width: 74mm;
  box-sizing: border-box;
  font-family: Arial, sans-serif;
  font-size: 9px;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.voucher-print-root p {
  margin: 0;
}
.voucher-print-root img {
  max-width: 100%;
}
`

/** Evita que los CSS globales del SPA (100vh / min-height en #root) alarguen la página al imprimir en Electron. */
const VOUCHER_PRINT_LAYOUT_RESET_CSS = `
@page {
  size: auto;
  margin: 0;
}
html {
  height: auto !important;
  min-height: 0 !important;
  max-height: none !important;
  margin: 0;
  padding: 0;
}
body {
  height: auto !important;
  min-height: 0 !important;
  max-height: none !important;
  margin: 0 !important;
  padding: 0 !important;
  overflow: visible !important;
}
#root,
[id^="root"] {
  height: auto !important;
  min-height: 0 !important;
  max-height: none !important;
}
.voucher-print-root {
  height: auto !important;
  min-height: 0 !important;
  max-height: none !important;
  overflow: visible !important;
}
`

/** Refuerzo para tickets: neutraliza min-height/vh heredados del bundle sin quitar el CSS del ticket. */
const TICKET_PRINT_CONTAINMENT_CSS = `
.voucher-print-root,
.voucher-print-root * {
  min-height: 0 !important;
  max-height: none !important;
}
[class*="section-full"],
[class*="section-flex"],
[class*="page-enter"],
[class*="page-exit"] {
  height: auto !important;
  min-height: 0 !important;
  max-height: none !important;
}
`

function escapeHtmlAttr (value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

/**
 * Resuelve src de imágenes contra la URL de la app (logo CPagos importado por webpack).
 * Sin esto, en ventana data: o impresión nativa el logo puede no cargar.
 */
function absolutizeImgSrcInFragment (innerHTML, baseHref) {
  if (!baseHref || typeof document === 'undefined') {
    return typeof innerHTML === 'string' ? innerHTML : ''
  }
  try {
    const wrap = document.createElement('div')
    wrap.innerHTML = innerHTML
    wrap.querySelectorAll('img[src]').forEach((img) => {
      const src = img.getAttribute('src')
      if (!src) return
      try {
        img.setAttribute('src', new URL(src, baseHref).href)
      } catch (_) {
        /* mantener src original */
      }
    })
    return wrap.innerHTML
  } catch (_) {
    return typeof innerHTML === 'string' ? innerHTML : ''
  }
}

function collectStylesheetLinks (baseHref, options = {}) {
  if (typeof document === 'undefined') return ''
  try {
    let links = Array.from(document.querySelectorAll('link[rel="stylesheet"][href]'))
    // Tickets: omitir hojas de estilo de vendors pesados si existen (conservador; el main chunk sigue).
    if (options.printKind === 'ticket') {
      links = links.filter((l) => {
        const href = String(l.getAttribute('href') || '').toLowerCase()
        if (href.includes('leaflet') || href.includes('mapbox')) return false
        return true
      })
    }
    return links
      .map((l) => {
        const href = l.getAttribute('href')
        if (!href) return ''
        try {
          const abs = baseHref ? new URL(href, baseHref).href : href
          return `<link rel="stylesheet" href="${escapeHtmlAttr(abs)}" />`
        } catch (_) {
          return `<link rel="stylesheet" href="${escapeHtmlAttr(href)}" />`
        }
      })
      .join('')
  } catch (_) {
    return ''
  }
}

/**
 * @param {string} innerHTML - innerHTML del nodo voucher ya renderizado por React (`processFormat`).
 * @param {{ baseHref?: string }} [options]
 * @returns {string}
 */
export function buildVoucherPrintDocument (innerHTML, options = {}) {
  const baseHref = options.baseHref || ''
  const baseTag = baseHref
    ? `<base href="${escapeHtmlAttr(baseHref)}" />`
    : ''
  const printKind = options.printKind === 'ticket' ? 'ticket' : 'voucher'
  const styleLinks = collectStylesheetLinks(baseHref, { printKind })
  const raw = typeof innerHTML === 'string' ? innerHTML : ''
  const body = absolutizeImgSrcInFragment(raw, baseHref)
  const ticketContainment =
    printKind === 'ticket' ? `<style>${TICKET_PRINT_CONTAINMENT_CSS}</style>` : ''
  return `<!DOCTYPE html><html><head><meta charset="utf-8"/>${baseTag}${styleLinks}<style>${VOUCHER_PRINT_CSS}</style><style>${VOUCHER_PRINT_LAYOUT_RESET_CSS}</style>${ticketContainment}</head><body><div class="voucher-print-root">${body}</div></body></html>`
}
